import React from 'react';
import { Form } from 'react-bootstrap';
import { useCandidatesCache } from '../hooks/use-candidates-cache';
import moment from 'moment';
import { useCourseEnrollees } from '../hooks/use-course-enrollees';
import { useSelectedStudents } from '../hooks/use-selected-students';
import { find } from 'lodash';
import { courseIsDuringHour } from '../utilities/course-is-during-hour';
import { courseEnrolleeCount } from '../utilities/course-enrollee-count';

const useCourseSelector = ({ student }) => {
    const { courses } = useCandidatesCache();
    const {
        courseEnrollees,
        setStudentCourseId,
        getStudentCourseId
    } = useCourseEnrollees();
    const { isStudentSelected } = useSelectedStudents();

    const value = React.useMemo(() => {
        return getStudentCourseId(student) || '';
    }, [getStudentCourseId, student]);

    const getOptionLabel = React.useCallback((course) => {
        const enrolleeCount = courseEnrolleeCount(course, courseEnrollees);
        return `${course.name} - ${moment(course.date).format('ddd MMM Do')} - ${enrolleeCount}/${course?.maxCapacity}`;
    }, [courseEnrollees, student]);

    const onChange = React.useCallback(
        (e) => setStudentCourseId(student, e.target.value), 
        [student, setStudentCourseId]
    );
    const options = React.useMemo(() => courses.filter(courseIsDuringHour(student?.hourId)), [courses, student?.hourId]);
    const hasOptions = options.length;
    const placeholder = hasOptions ? 'Select Experience' : 'No Available Experiences'
    const disabled = !isStudentSelected(student) || !hasOptions;
    return {
        value,
        getOptionLabel,
        disabled,
        options,
        hasOptions,
        placeholder,
        onChange,
    };
};

export const CourseSelector = ({ student }) => {
    const {
        value,
        getOptionLabel,
        disabled,
        options,
        hasOptions,
        placeholder,
        onChange
    } = useCourseSelector({ student });

    const renderCourse = ((course, key) => (
        <option key={key} value={course.id}>
            {getOptionLabel(course)}
        </option>
    ));

    return (
        <Form.Control
            as="select"
            size='sm'
            value={value}
            onChange={onChange}
            disabled={disabled}
        >
            <option value='' disabled={hasOptions} >{placeholder}</option>
            {options.map(renderCourse)}    
        </Form.Control>
    );
};
